import { type LocaleType } from '@landsbankinn/l16n';
import {
  FormSubmitServiceToken,
  type IFormSubmitService,
} from 'src/services/FormSubmitService';
import { ServicesProvider } from 'src/services/ServicesProvider';
import {
  AllowedLoginType,
  type ApplicationClientActionType,
  type ApplicationClientStateType,
  type LoginServerState,
} from '../../state';

const PREVIOUSLOGINTYPE_KEY = 'idp.previousLoginType';

export function setLoginTypeAndReset(
  loginType: AllowedLoginType | null,
  previousLoginType: AllowedLoginType | null,
  dispatch: (value: ApplicationClientActionType) => void,
) {
  // We don't want any state changes if the login type is the same as before
  // See ticket https://jira.lais.net/browse/WEB-39672
  if (loginType === previousLoginType) return;

  dispatch({ type: 'setLoginType', payload: { loginType } });
  dispatch({ type: 'clearFormValues' });
  dispatch({ type: 'clearServerErrorState' });
  clearPreviousLoginType();
}

function getCorrectLoginType(
  clientState: ApplicationClientStateType,
  serverState: LoginServerState,
): AllowedLoginType | null {
  // Determining the correct login type depends on three values: currentLoginType in clientState,
  // the previousLoginType set in localStorage when the form was last submitted and the number of
  // allowed login types available in the serverState.

  // If the clientstate contains the currentLoginType then that is the correct current login type.
  let loginType = clientState.currentLoginType;

  if (loginType === null) {
    if (serverState.allowedLoginTypes?.length === 1) {
      // If there is only one available login type then that is the correct login type
      loginType = serverState.allowedLoginTypes[0];
    } else {
      // When form is submitted we store the last used login type
      const previousLoginType = getPreviousLoginType();

      if (clientState.errors && previousLoginType) {
        // If there are any errors in our initial server login state we use the last known login type
        // to show the error in its correct place.
        loginType = previousLoginType;
      } else if (
        !clientState.errors &&
        previousLoginType &&
        serverState.allowedLoginTypes &&
        // this is important because some re-authentications might not allow the previous login type
        serverState.allowedLoginTypes.includes(previousLoginType)
      ) {
        // If there are no errors then set the login type as the previous login type only if it is not password
        // That is because we want to deter users from using this type of authentication
        if (previousLoginType === AllowedLoginType.Password)
          loginType = getOrderedLoginTypes(serverState)[0];
        else loginType = previousLoginType;
      }
    }
  }
  return loginType;
}

export function getPreferredLoginTypeBasedOnAllowedLoginTypes(
  state: LoginServerState,
): AllowedLoginType {
  if (state.allowedLoginTypes !== undefined) {
    if (state.allowedLoginTypes.includes(AllowedLoginType.App))
      return AllowedLoginType.App;
    if (state.allowedLoginTypes.includes(AllowedLoginType.PkiSim))
      return AllowedLoginType.PkiSim;
    if (state.allowedLoginTypes.includes(AllowedLoginType.AudkenniApp))
      return AllowedLoginType.AudkenniApp;
  }

  return AllowedLoginType.Password;
}

export function getCorrectLoginTypeOr(
  clientState: ApplicationClientStateType,
  serverState: LoginServerState,
  preferred: AllowedLoginType,
) {
  return getCorrectLoginType(clientState, serverState) || preferred;
}

export function getPreviousLoginType(): AllowedLoginType | null {
  return localStorage.getItem(
    `${PREVIOUSLOGINTYPE_KEY}`,
  ) as AllowedLoginType | null;
}

function clearPreviousLoginType(): void {
  localStorage.removeItem(`${PREVIOUSLOGINTYPE_KEY}`);
}

export function setPreviousLoginType(loginType: AllowedLoginType): void {
  localStorage.setItem(`${PREVIOUSLOGINTYPE_KEY}`, loginType);
}

export function getOrderedLoginTypes(
  state: LoginServerState,
): AllowedLoginType[] {
  const preferredLoginTypeOrder = [
    AllowedLoginType.App,
    AllowedLoginType.PkiSim,
    AllowedLoginType.AudkenniApp,
    AllowedLoginType.Password,
  ];
  const items = state.allowedLoginTypes
    .slice()
    .sort(
      (a, b) =>
        preferredLoginTypeOrder.indexOf(a) - preferredLoginTypeOrder.indexOf(b),
    );

  return items;
}

export function submit(form: HTMLFormElement) {
  setTimeout(() => {
    const formSubmitService = ServicesProvider.getSingleton<IFormSubmitService>(
      FormSubmitServiceToken,
    );

    formSubmitService.submitForm(form);
  }, 100);
}

export function submitAccessDenied(
  dispatch: (value: ApplicationClientActionType) => void,
  form?: HTMLFormElement,
) {
  dispatch({ type: 'setIsSubmitting', payload: { isSubmittingCancel: true } });
  if (!form) form = document.getElementsByTagName('form')[0] as HTMLFormElement;
  form.action = '/ui/login/cancel';
  const formSubmitService = ServicesProvider.getSingleton<IFormSubmitService>(
    FormSubmitServiceToken,
  );
  formSubmitService.submitForm(form);
}

const COMPANY_KENNITALA_FIRST_DIGITS = [4, 5, 6, 7];
const KENNITALA_LENGTH = 10;
export const isCorporateKennitala = (kennitala?: string | null) => {
  return (
    kennitala &&
    isKennitala(kennitala) &&
    COMPANY_KENNITALA_FIRST_DIGITS.includes(Number(kennitala[0]))
  );
};
/**
 * Function to validate a kennitala
 * @param kennitala Kennitala to check
 * @returns Boolean indicating whether kennitala is valid or not
 */
export const isKennitala = (kennitala?: string | null) => {
  // if value is empty
  if (!kennitala) {
    return false;
  }

  // if value is not of valid format (length = 10)
  if (kennitala.length !== KENNITALA_LENGTH) {
    return false;
  }
  // if value contains non-numeric characters
  if (/^\d+$/.exec(kennitala) === null) {
    return false;
  }

  if (kennitala.startsWith('8') || kennitala.startsWith('9')) {
    return true;
  }

  const firstTwo = kennitala.substring(0, 2);
  const thirdAndFourth = kennitala.substring(2, 4);

  if (COMPANY_KENNITALA_FIRST_DIGITS.includes(Number(kennitala[0]))) {
    // Companies start with the first number 4 higher than persons
    const computedFirstTwo = Number(firstTwo) - 40;
    const paddedAndComputedFirstTwo =
      computedFirstTwo >= 10
        ? String(computedFirstTwo)
        : `0${computedFirstTwo}`;
    if (
      isNaN(Date.parse(`2000-${thirdAndFourth}-${paddedAndComputedFirstTwo}`))
    )
      return false;
  } else if (isNaN(Date.parse(`2000-${thirdAndFourth}-${firstTwo}`))) {
    return false;
  }

  return true;
};

export const getLocaleToLanguage = (lang: LocaleType | undefined) => {
  switch (lang) {
    case 'en':
      return 'en-GB';
    case 'pl':
      return 'pl-PL';
    default:
      return 'is-IS';
  }
};
